/* eslint-disable react/no-multi-comp */
import React, { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { List, Typography } from '@material-ui/core';
import { NavigationListItem } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#b0bec5',
    padding: theme.spacing(0, 1.5, 0.75),
    display: 'block',
  },
}));

const Navigation = ({ title, pages, className, component: Component = 'nav', ...rest }) => {
  const classes = useStyles();

  return (
    <Component className={clsx(classes.root, className)} {...rest}>
      {title && <Typography className={classes.sectionTitle}>{title}</Typography>}
      <NavigationList pages={pages} />
    </Component>
  );
};

Navigation.propTypes = {
  className: PropTypes.string,
  component: PropTypes.any,
  pages: PropTypes.array.isRequired,
  title: PropTypes.string
};

export default Navigation;

// -------------------------------------------

const NavigationList = ({ pages, depth = 0 }) => {
  const location = useLocation();

  const items = useMemo(
    () =>
      pages.map(page => (
        <RenderItem key={page.title} page={page} depth={depth} location={location} />
      )),
    [pages, depth, location]
  );

  return <List>{items}</List>;
};

NavigationList.propTypes = {
  depth: PropTypes.number,
  pages: PropTypes.array.isRequired
};

// -------------------------------------------

const RenderItem = ({ page, depth, location }) => {
  const hasChildren = Array.isArray(page.children) && page.children.length > 0;

  const commonProps = {
    depth,
    icon: page.icon,
    label: page.label,
    title: page.title
  };

  if (hasChildren) {
    const match = page.href
      ? matchPath({ path: page.href, end: false }, location.pathname)
      : null;

    return (
      <NavigationListItem {...commonProps} open={Boolean(match) || Boolean(page.defaultOpen)}>
        <NavigationList pages={page.children} depth={depth + 1} />
      </NavigationListItem>
    );
  }

  if (!page.href) {
    console.warn(`Navigation item "${page.title}" is missing an 'href'. Skipping render.`);
    return null;
  }

  return <NavigationListItem {...commonProps} href={page.href} />;
};

RenderItem.propTypes = {
  page: PropTypes.object.isRequired,
  depth: PropTypes.number.isRequired,
  location: PropTypes.object.isRequired
};
